import * as React from 'react';
import { useDispatch } from 'react-redux';
import { tryUpdateRelease, UpdateAdditionalSelectedBuildsRequest } from '../../actions/release';
import { ReleaseUpdatable } from '../../actions/updatable';
import { AppPlatform, AppStoreAvailableBuild, AppStoreType, RunwayResponseType } from '../../types';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import ChangeSelectedBuildModalBase from '@/components/selectedAppStoreBuild/ChangeSelectedBuildsModalBase';
import Text from 'antd/lib/typography/Text';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable, UpdateAdditionalSelectedBuildsInAppStoreRequest } from '@/actions/stepRun';

interface ChangeAdditionalBuildsModalProps extends ReleaseComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    includeAssociatedCIBuilds: boolean;
    open: boolean;
    currentlySelectedAdditionalAppStoreBuilds?: AppStoreAvailableBuild[];
    availableBuilds: AppStoreAvailableBuild[];
    onClose: () => void;
    onSuccess: () => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const ChangeAdditionalBuildsModal: React.FC<ChangeAdditionalBuildsModalProps> = (
    props: ChangeAdditionalBuildsModalProps,
) => {
    const dispatch = useDispatch();

    const updateAdditionalSelectedBuildsSuccessReleaseStep = useUpdated(
        ReleaseUpdatable.UpdateAdditionalSelectedBuilds,
        props.releaseId,
    );
    const updateAdditionalSelectedBuildsIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.UpdateAdditionalSelectedBuilds,
        props.releaseId,
    );
    const updateAdditionalSelectedBuildsErrorReleaseStep = useError(
        ReleaseUpdatable.UpdateAdditionalSelectedBuilds,
        props.releaseId,
    );

    const [
        updateAdditionalSelectedBuildsStepRun,
        updateAdditionalSelectedBuildsIsLoadingStepRun,
        updateAdditionalSelectedBuildsResponseTypeStepRun,
        updateAdditionalSelectedBuildsErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.SetAdditionalSelectedBuilds, props.stepRunComponentProps);

    const updateSuccess =
        props.stepRunComponentProps ?
            updateAdditionalSelectedBuildsResponseTypeStepRun === RunwayResponseType.Success
        :   updateAdditionalSelectedBuildsSuccessReleaseStep;

    const updateError =
        props.stepRunComponentProps ?
            updateAdditionalSelectedBuildsErrorStepRun
        :   updateAdditionalSelectedBuildsErrorReleaseStep;

    const updateIsLoading =
        props.stepRunComponentProps ?
            updateAdditionalSelectedBuildsIsLoadingStepRun
        :   updateAdditionalSelectedBuildsIsLoadingReleaseStep;

    const onSaveChanges = (buildIds: string[]) => {
        if (props.stepRunComponentProps) {
            const request: UpdateAdditionalSelectedBuildsInAppStoreRequest = {
                buildIds: buildIds,
            };
            updateAdditionalSelectedBuildsStepRun(request);
        } else {
            const request: UpdateAdditionalSelectedBuildsRequest = {
                orgId: props.orgId,
                buildIds: buildIds,
                appId: props.appId,
                releaseId: props.releaseId,
            };
            dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateAdditionalSelectedBuilds, request, props.releaseId));
        }
    };

    const title = 'Include build(s) from a previous release';
    const label = 'Additional build(s) to include';

    React.useEffect(() => {
        if (updateSuccess) {
            globalMessage.success('Additional builds updated successfully');
            props.onSuccess();
        }
    }, [updateSuccess]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    return (
        <ChangeSelectedBuildModalBase
            platform={props.platform}
            appStoreType={props.appStoreType}
            title={title}
            label={label}
            isLoading={updateIsLoading}
            includeAssociatedCIBuilds={props.includeAssociatedCIBuilds}
            showRecentBuilds={false}
            showPastBuildsEmptyState={true}
            open={props.open}
            availableBuilds={props.availableBuilds}
            onSaveChanges={onSaveChanges}
            onClose={props.onClose}
            onSuccess={props.onSuccess}
            releaseId={props.releaseId}
            appId={props.appId}
            orgId={props.orgId}
            showsClearButton
            currentSelection={props.currentlySelectedAdditionalAppStoreBuilds}
            footer={
                <Text type="secondary">
                    Retained builds will be automatically carried over to future releases. If you wish to stop retaining
                    these builds, clear them from this field and save.
                </Text>
            }
        />
    );
};

export default ChangeAdditionalBuildsModal;
